/**
 * Admin → Developer API (#793): the session endpoints that manage org API keys,
 * plus the scope vocabulary the UI shows.
 *
 * The key endpoints live under `/api/admin/public-api/*` and need an admin with
 * MFA. The keys themselves are spent on `/public/v1` — the only call this page
 * makes there is "Send test request", which uses a key the admin just saw.
 */
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api/admin/public-api`;

/** Where outside apps send requests. Same host as the CRM. */
export function publicBaseUrl() {
    const origin = BACKEND || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${origin}/public/v1`;
}

export const ACTIONS = ['read', 'create', 'update', 'delete'];

/** Resources in the scope matrix, in display order. Mirrors backend `public_api/scopes.py`. */
export const RESOURCES = [
    { key: 'contacts', label: 'Contacts', actions: ACTIONS },
    { key: 'companies', label: 'Companies', actions: ACTIONS },
    { key: 'users', label: 'Team members', actions: ['read'], note: 'Names, emails and Referral IDs' },
    { key: 'events', label: 'Timeline events', actions: ['create'], note: 'Activity on a contact’s timeline' },
];

export const ALL_SCOPES = RESOURCES.flatMap((r) => r.actions.map((a) => `${r.key}:${a}`));

const PARTNER = ['contacts:read', 'contacts:create', 'contacts:update', 'companies:create', 'users:read', 'events:create'];

export const PRESETS = [
    { id: 'lead', title: 'Lead capture', description: 'Create contacts and companies', scopes: ['contacts:create', 'companies:create'] },
    { id: 'partner', title: 'Partner app', description: 'Leads, referrals and timeline events', scopes: PARTNER },
    {
        id: 'sync',
        title: 'Two-way sync',
        description: 'Read, create and update records',
        scopes: ['contacts:read', 'contacts:create', 'contacts:update', 'companies:read', 'companies:create', 'companies:update'],
    },
    { id: 'custom', title: 'Custom', description: 'Pick each permission', scopes: null },
];

/** Keep scopes in registry order, so lists and chips read the same everywhere. */
export function orderScopes(scopes) {
    const set = new Set(scopes);
    return ALL_SCOPES.filter((s) => set.has(s));
}

/**
 * Toggle one scope. Update and delete imply read — a key that edits can always
 * look up what it edits — so ticking either adds read, and unticking read
 * removes them.
 */
export function toggleScope(scopes, scope) {
    const next = new Set(scopes);
    const [resource, action] = scope.split(':');
    if (next.has(scope)) {
        next.delete(scope);
        if (action === 'read') {
            next.delete(`${resource}:update`);
            next.delete(`${resource}:delete`);
        }
    } else {
        next.add(scope);
        if ((action === 'update' || action === 'delete') && ALL_SCOPES.includes(`${resource}:read`)) {
            next.add(`${resource}:read`);
        }
    }
    return orderScopes([...next]);
}

export function presetFor(scopes) {
    const preset = PRESETS.find(
        (p) => p.scopes && p.scopes.length === scopes.length && p.scopes.every((s) => scopes.includes(s)),
    );
    return preset ? preset.id : 'custom';
}

/** "This key can read, create contacts and create companies." */
export function scopeSentence(scopes) {
    if (!scopes || !scopes.length) return 'No permissions selected yet.';
    const parts = RESOURCES.map((r) => {
        const acts = ACTIONS.filter((a) => scopes.includes(`${r.key}:${a}`));
        return acts.length ? `${acts.join(', ')} ${r.label.toLowerCase()}` : null;
    }).filter(Boolean);
    return `This key can ${parts.join(' and ')}.`;
}

export function sourceTagFor(name) {
    const slug = String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return `api:${slug || 'key'}`;
}

export const EXPIRY_OPTIONS = [
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: '1 year', days: 365 },
    { label: 'Never', days: null },
];

export const ROLL_GRACE = [
    { value: '0', label: 'Stop now' },
    { value: '1h', label: '1 hour' },
    { value: '24h', label: '24 hours' },
    { value: '7d', label: '7 days' },
];

export function daysUntil(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Number.isFinite(ms) ? Math.ceil(ms / 86400000) : null;
}

export function relativeTime(iso) {
    if (!iso) return 'Never';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '—';
    const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDate(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── API ──────────────────────────────────────────────────────────────────────

export async function listKeys(headers, { includeRevoked = true } = {}) {
    const res = await axios.get(`${API}/keys`, { headers, params: { include_revoked: includeRevoked } });
    return res.data?.data || [];
}

export async function getKey(headers, keyId) {
    return (await axios.get(`${API}/keys/${encodeURIComponent(keyId)}`, { headers })).data;
}

export async function createKey(headers, body) {
    return (await axios.post(`${API}/keys`, body, { headers })).data;
}

export async function updateKey(headers, keyId, body) {
    return (await axios.patch(`${API}/keys/${encodeURIComponent(keyId)}`, body, { headers })).data;
}

export async function revokeKey(headers, keyId) {
    return (await axios.delete(`${API}/keys/${encodeURIComponent(keyId)}`, { headers })).data;
}

export async function rollKey(headers, keyId, grace) {
    return (await axios.post(`${API}/keys/${encodeURIComponent(keyId)}/roll`, { grace }, { headers })).data;
}

export async function listRequests(headers, params = {}) {
    const res = await axios.get(`${API}/requests`, { headers, params });
    return {
        rows: res.data?.data || [],
        nextBefore: res.data?.next_before || null,
        nextBeforeId: res.data?.next_before_id || null,
    };
}

export async function getStats(headers) {
    return (await axios.get(`${API}/stats`, { headers })).data;
}

// ── Webhook endpoints (#793 S7) ─────────────────────────────────────────────

const HOOKS = `${BACKEND}/api/admin/webhook-endpoints`;

export async function listWebhooks(headers) {
    const res = await axios.get(HOOKS, { headers });
    return {
        endpoints: res.data?.data || [],
        events: res.data?.events || [],
        max: res.data?.max_endpoints ?? 10,
        signingAvailable: res.data?.signing_available !== false,
    };
}

export async function createWebhook(headers, body) {
    return (await axios.post(HOOKS, body, { headers })).data;
}

export async function updateWebhook(headers, endpointId, body) {
    return (await axios.patch(`${HOOKS}/${encodeURIComponent(endpointId)}`, body, { headers })).data;
}

export async function deleteWebhook(headers, endpointId) {
    return (await axios.delete(`${HOOKS}/${encodeURIComponent(endpointId)}`, { headers })).data;
}

export async function webhookAction(headers, endpointId, action) {
    // action: 'pause' | 'resume' | 'rotate-secret' | 'test'
    return (await axios.post(`${HOOKS}/${encodeURIComponent(endpointId)}/${action}`, {}, { headers })).data;
}

export async function listDeliveries(headers, params = {}) {
    const res = await axios.get(`${HOOKS}/deliveries`, { headers, params });
    return res.data?.data || [];
}

export async function resendDelivery(headers, deliveryId) {
    return (await axios.post(`${HOOKS}/deliveries/${encodeURIComponent(deliveryId)}/resend`, {}, { headers })).data;
}

export async function listTeam(headers) {
    const res = await axios.get(`${BACKEND}/api/accounts/team-members`, { headers });
    // A member still pending approval cannot be a key's default owner (the
    // backend answers 422 INVALID_DEFAULT_OWNER), so they are not offered.
    return (Array.isArray(res.data) ? res.data : []).filter((m) => m.approval_status !== 'pending');
}

/**
 * "Send test request": call `GET /public/v1/me` with the key just shown. The key
 * goes in the Authorization header only — never a URL — and is not stored.
 */
export async function testKey(rawKey) {
    const started = Date.now();
    const res = await axios.get(`${publicBaseUrl()}/me`, {
        headers: { Authorization: `Bearer ${rawKey}` },
        validateStatus: () => true,
    });
    return { status: res.status, data: res.data, ms: Date.now() - started };
}
